import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { call, put, select, takeEvery } from 'redux-saga/effects';
import { RootState } from '.';
import api from '../api/BlancoClient';
import { extractInvalidChars } from '../lib/message-validator';
import { ChatMessageType } from '../models/ChatMessageType';
import { actions as userActions, getFreshToken } from './user';
import { checkErrorType } from './utils/helper';

export type ChatFormErrorType = 'text-is-too-long' | 'invalid-char';
type ChatNetworkErrorType =
  | 'im-offline'
  | 'communication-disabled'
  | 'dest-offline'
  | 'unstable-network'
  | 'rate-limit-exceeded'
  | 'unknown';
export type ChatErrorType = ChatFormErrorType | ChatNetworkErrorType;

const textCountLimit = 32;
const textWidthLimit = 770;

export interface ChatErrorPayload {
  invalidChars?: string[];
}

export interface ChatState {
  message: string;
  messageWidth: number;
  isSending?: boolean;
  sentLatest?: string;
  warnTypes: ChatFormErrorType[];
  errorTypes: ChatErrorType[];
  errorPayload?: ChatErrorPayload;
}

const initialState: ChatState = {
  message: '',
  messageWidth: 0,
  warnTypes: [],
  errorTypes: []
};

const slice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    updateMessage: (draft, { payload }: PayloadAction<string>) => {
      draft.message = payload;
      const invalidChars = extractInvalidChars(payload);
      if (invalidChars) {
        if (!draft.warnTypes.find(wt => wt === 'invalid-char')) {
          draft.warnTypes.push('invalid-char');
        }
      } else {
        draft.warnTypes = draft.warnTypes.filter(wt => wt !== 'invalid-char');
      }
      draft.errorPayload = { ...draft.errorPayload, invalidChars };
    },

    updateMessageWidth: (state, { payload }: PayloadAction<{ width: number }>) => {
      const count = Array.from(state.message).length;
      const { width } = payload;
      const isTooLong = count > textCountLimit || width > textWidthLimit;
      if (isTooLong) {
        if (!state.warnTypes.find(wt => wt === 'text-is-too-long')) {
          state.warnTypes.push('text-is-too-long');
        }
      } else {
        state.warnTypes = state.warnTypes.filter(wt => wt !== 'text-is-too-long');
      }
    },

    fillPrevMessage: draft => {
      const { message, sentLatest } = draft;
      draft.message = `${message}${sentLatest || ''}`;
      draft.sentLatest = undefined;
    },

    postMessage: (
      state,
      _: PayloadAction<{ message: string; type: ChatMessageType; destination?: string }>
    ) => {
      state.isSending = true;
    },

    postMessageSucceeded: draft => {
      draft.sentLatest = draft.message;
      draft.message = '';
      draft.isSending = false;
    },

    postMessageFailed: (
      draft,
      { payload }: PayloadAction<{ errorTypes: ChatErrorType[]; errorPayload?: ChatErrorPayload }>
    ) => {
      draft.isSending = false;
      draft.errorTypes = payload.errorTypes;
      draft.errorPayload = payload.errorPayload;
    },

    clearError: draft => {
      draft.errorTypes = [];
    },

    reset: draft => {
      draft.message = initialState.message;
      draft.messageWidth = initialState.messageWidth;
      draft.isSending = initialState.isSending;
      draft.sentLatest = initialState.sentLatest;
      draft.warnTypes = initialState.warnTypes;
      draft.errorTypes = initialState.errorTypes;
      draft.errorPayload = initialState.errorPayload;
    }
  }
});

export default slice.reducer;

const {
  updateMessage,
  updateMessageWidth,
  fillPrevMessage,
  postMessage,
  postMessageSucceeded,
  postMessageFailed,
  clearError,
  reset
} = slice.actions;

export const actions = {
  updateMessage,
  updateMessageWidth,
  fillPrevMessage,
  postMessage,
  postMessageSucceeded,
  postMessageFailed,
  clearError,
  reset
};

function* postMessageSaga(action: {
  type: typeof postMessage.type;
  payload: {
    message: string;
    type: ChatMessageType;
    destination?: string;
  };
}) {
  const { warnTypes, errorPayload } = yield select((state: RootState) => state.chat);
  if (warnTypes.length > 0) {
    yield put(postMessageFailed({ errorTypes: warnTypes, errorPayload }));
    return;
  }

  try {
    const baid: string = yield call(getFreshToken);
    const { message, type, destination } = action.payload;
    yield call(api.postMessage, message, type, destination, baid);
    yield put(postMessageSucceeded());
  } catch (error) {
    const errorType = checkErrorType(error);
    if (errorType === 'server') {
      yield put(userActions.causeServerError(error.response.data?.code));
    } else if (errorType === 'unstable-network') {
      yield put(postMessageFailed({ errorTypes: ['unstable-network'] }));
    } else {
      let chatErrorType: ChatErrorType;
      if (error.response?.data) {
        const { data } = error.response;
        chatErrorType = getChatErrorType(data.code);
      } else {
        chatErrorType = 'unknown';
      }
      yield put(postMessageFailed({ errorTypes: [chatErrorType] }));
    }
  }
}

export function* chatSaga() {
  yield takeEvery(postMessage.type, postMessageSaga);
}

function getChatErrorType(code?: string) {
  switch (code) {
    case '1001':
      return 'im-offline';
    case '1002':
      return 'communication-disabled';
    case '1003':
      return 'dest-offline';
    case '3007':
      return 'rate-limit-exceeded';
    default:
      return 'unknown';
  }
}
